const DEFAULT_BACKEND = 'https://us-central1-holostaff-ai.cloudfunctions.net'

// ---------------------------------------------------------------------------
// Response types (match widgetSession Cloud Function output)
// ---------------------------------------------------------------------------

export interface StaffConfig {
  name: string
  persona: string | null
  instructions: string | null
  openingText: string | null
  voiceId: string
}

export interface WidgetSessionResponse {
  realtimeEphemeralKey: string
  realtimeWebrtcUrl: string
  anamSessionToken: string
  staff: StaffConfig
  /** Full Markdown instructions keyed by skill name. Served to the get_skill_instructions tool. */
  skills: Record<string, string>
}

export interface StaffPublicConfig {
  name: string
  persona: string | null
  anamAvatarId: string | null
  avatarImageUrl: string | null
  idleVideoUrl: string | null
  openingText: string | null
  widgetPrompts: string[] | null
  voiceId: string
  // Widget display settings (from RTDB, used as defaults if embed attrs not set)
  trigger: string | null
  position: string | null
  theme: string | null
  scrollThreshold: number | null
  delayMs: number | null
  visionWsUrl: string | null
  pagePatterns: (string | { pattern: string; container?: string })[] | null
  /** True when the visitor has been flagged — widget must suppress entirely. */
  blocked?: boolean
  /** True for eval sessions (visitorId starts with "eval_"). Widget exposes __HS_EVAL_STATE__. */
  evalMode?: boolean
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function fetchWidgetSession(
  staffId: string,
  backendUrl = DEFAULT_BACKEND,
  visitorId?: string,
): Promise<WidgetSessionResponse> {
  const res = await fetch(`${backendUrl}/widgetSession`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffId, visitorId }),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(body.error || `Session request failed: ${res.status}`)
  }

  return res.json()
}

export class WidgetDisabledError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'WidgetDisabledError'
  }
}

export async function fetchWidgetConfig(
  staffId: string,
  backendUrl = DEFAULT_BACKEND,
  visitorId?: string,
): Promise<StaffPublicConfig> {
  let url = `${backendUrl}/widgetConfig?staffId=${encodeURIComponent(staffId)}`
  if (visitorId) url += `&visitorId=${encodeURIComponent(visitorId)}`
  const res = await fetch(url)

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    // 404 with disabled:true = agent deleted or inactive — caller must bail entirely
    if (body.disabled === true || res.status === 404) {
      throw new WidgetDisabledError(body.error || 'Agent not available')
    }
    throw new Error(body.error || `Config request failed: ${res.status}`)
  }

  return res.json()
}

export async function sendKeepalive(
  sessionId: string,
  sessionToken: string,
  backendUrl = DEFAULT_BACKEND,
): Promise<void> {
  const res = await fetch(`${backendUrl}/widgetKeepalive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, sessionToken }),
  })

  if (!res.ok) {
    console.warn('[backend] Keep-alive failed:', res.status)
  }
}

export interface PageActionResponse {
  actions: Array<{
    action: string
    index: number
    value?: string
    description: string
  }>
}

export async function flagVisitor(
  staffId: string,
  visitorId: string,
  reason: string,
  backendUrl = DEFAULT_BACKEND,
): Promise<void> {
  try {
    await fetch(`${backendUrl}/flagVisitor`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ staffId, visitorId, reason }),
    })
  } catch (err) {
    console.warn('[backend] flagVisitor failed (fire-and-forget):', err)
  }
}

export async function postPageAction(
  staffId: string,
  domSnapshot: string,
  instruction: string,
  backendUrl = DEFAULT_BACKEND,
): Promise<PageActionResponse> {
  const res = await fetch(`${backendUrl}/widgetPageAction`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffId, domSnapshot, instruction }),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(body.error || `Page action request failed: ${res.status}`)
  }

  return res.json()
}
